/**
 * Mirror of the authenticated identity.
 *
 * The source of truth is the TanStack Query `session` entry; this store exists
 * so non-React code and deeply nested components can read the current
 * principal without threading props or re-querying.
 */
import { create } from 'zustand';

import type { Session } from '../api/types';

interface SessionState {
  session: Session | null;
  status: 'unknown' | 'authenticated' | 'anonymous';
  setSession: (session: Session | null) => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  session: null,
  status: 'unknown',
  setSession: (session) => set({ session, status: session ? 'authenticated' : 'anonymous' }),
}));

/** isAdmin reports whether the current principal may perform admin writes. */
export function isAdmin(session: Session | null): boolean {
  return session?.role === 'admin';
}

/** useIsAdmin is the UI gate for every write affordance. The server enforces
 * the same rule; this only keeps readonly users from seeing dead buttons. */
export function useIsAdmin(): boolean {
  return useSessionStore((s) => isAdmin(s.session));
}
